const post = {
  slug: "xgot-expected-goals-on-target-explained",
  title: "xGOT Explained: Expected Goals on Target",
  category: "Analysis",
  date: "Oct 13, 2026",
  readTime: "5 min read",
  excerpt:
    "xG tells you how good a chance was. xGOT tells you how good the shot was. Here's what expected goals on target measures, how it differs from xG, and what the gap between them says about finishers and goalkeepers.",
  content: [
    "Two strikers get the same chance: one-on-one, ten yards out, central. One rolls it into the bottom corner. The other hits it straight at the goalkeeper's chest. On expected goals (xG) those two shots are identical, because xG only looks at the chance. Expected goals on target, or xGOT, is the metric that tells them apart. It's now shown on every shot on target in Goal Guru.",

    "## A Quick Recap of xG",

    "Expected goals gives every shot a value between 0 and 1: how often a shot like that, from that position and in that situation, usually goes in. It's measured at the moment the shot is taken, before anyone knows where the ball will go. That makes it a measure of **chance quality**: how good an opportunity a team created. If you're new to it, start with our [guide to xG](/blog/expected-goals-xg-explained/).",

    "## What xGOT Adds",

    "xGOT is measured after the shot is struck, and only for shots on target. On top of the chance itself, it takes into account where the ball was headed: how close to the post or the bar, and how hard that spot is for a goalkeeper to reach. A shot into the top corner gets a high xGOT. A shot straight down the middle, however good the chance, gets a low one.",

    "So the two metrics answer different questions:",

    "- **xG:** how good was the chance?\n- **xGOT:** how good was the shot, given where it went?",

    "Shots that miss the target or get blocked have no xGOT, because they can't trouble the goalkeeper. That's why xGOT appears only on shots on target.",

    "## What the Gap Between Them Tells You",

    "### About finishers",

    "When a player's shots carry more xGOT than xG, they're placing the ball better than an average shot from those positions. They're turning ordinary chances into hard-to-save shots. Consistently less xGOT than xG points the other way: decent chances, poorly placed shots. Over a single match this is noise. Over a season, it's one of the clearer signs of finishing quality.",

    "### About goalkeepers",

    "xGOT is also the fairest way to judge a goalkeeper. Compare the xGOT a keeper faced with the goals they actually conceded. A keeper who faced 2.5 xGOT and let in one goal made saves an average keeper wouldn't have. One who conceded three from 1.0 xGOT let in shots that are usually saved. Raw save percentage can't tell those situations apart.",

    "### About a single match",

    "Within one game, xGOT tells you whether a team's chances actually tested the goalkeeper. A side can rack up xG through a pile of half-chances that never hit the target, or through a handful of shots that all forced saves. The second is far closer to scoring.",

    "## Where You'll Find It in Goal Guru",

    "- **Shot map:** tap any shot to see the distance and angle it was struck from and its xG. For shots on target, its xGOT is shown too.\n- **Timeline:** every goal carries the xG of the chance it came from, so you can tell a well-worked tap-in from a long-range strike at a glance.\n- **Match stats:** an xGOT row sits next to xG for both teams.",

    "The [Lineups & Shot Map](/features/lineups-shot-map/) page covers these views in more detail, along with the xG timeline chart that shows how each team's chances built up over the match.",

    "## Putting It to Work",

    "xGOT is a reading tool rather than an alert condition, but it pairs well with the stats you can alert on. A [Smart Alert](/smart-football-notifications/) on shots on target or xG flags the pressure as it builds. Opening the shot map then tells you whether that pressure is really testing the goalkeeper or just adding up on paper.",

    "Chances tell you who's creating. Shots on target tell you who's testing the goalkeeper. xGOT tells you how close each of those shots came to going in.",
  ],
};

export default post;
